import { FC } from "react";
import { Dialog } from "@headlessui/react";
import { DateTime } from "luxon";
import { FormikProps } from "formik";
import { InputWithCornerHint } from "../atoms/InputWithCornerHint";
import { Select } from "../organisms/Select";
import { DateRangePicker } from "../atoms/DataRangePicker";
import { MultiSelect } from "../organisms/MultiSelect";

export type BillsFilterProps = {
  setVisible: (visible: boolean) => void;
  visible?: boolean;
  formik: FormikProps<any>;
  currencies: Array<any>;
  branches: Array<any>;
  subcategories: Array<any>;
  personal: Array<any>;
};

export const BillsFilter: FC<BillsFilterProps> = ({
  setVisible,
  visible = false,
  formik,
  personal = [],
  currencies = [],
  branches = [],
  subcategories = [],
}) => {
  return (
    <Dialog
      open={visible}
      onClose={() => setVisible(false)}
      className="relative z-50"
    >
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50" aria-hidden="true" />

      {/* Modal wrapper */}
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="bg-white w-full max-w-4xl rounded-2xl shadow-xl p-6 space-y-6">
          {/* Close button */}
          <button
            type="button"
            onClick={() => setVisible(false)}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
          >
            <span className="sr-only">Cerrar</span>
            &times;
          </button>

          {/* Primera fila */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <MultiSelect
              selectedOptions={personal.filter((p) =>
                formik.values.createdByIds?.includes(p.id)
              )}
              setSelectedOptions={(
                selected: Array<{ id: number; name: string }>
              ) => {
                formik.setFieldValue(
                  "createdByIds",
                  selected.map((person) => person.id)
                );
                formik.submitForm();
              }}
              options={personal}
              title="Creado por"
            />
            <MultiSelect
              selectedOptions={personal.filter((p) =>
                formik.values.approvedByIds?.includes(p.id)
              )}
              setSelectedOptions={(
                selected: Array<{ id: number; name: string }>
              ) => {
                formik.setFieldValue(
                  "approvedByIds",
                  selected.map((person) => person.id)
                );
                formik.submitForm();
              }}
              options={personal}
              title="Aprobado por"
            />
            <DateRangePicker
              id="date"
              label="Fechas"
              value={{
                startDate: formik.values.startDate
                  ? DateTime.fromFormat(
                      formik.values.startDate,
                      "yyyy-MM-dd"
                    ).toJSDate()
                  : new Date(),
                endDate: formik.values.endDate
                  ? DateTime.fromFormat(
                      formik.values.endDate,
                      "yyyy-MM-dd"
                    ).toJSDate()
                  : new Date(),
              }}
              onDateChange={(startDate, endDate) => {
                formik.setFieldValue(
                  "startDate",
                  DateTime.fromJSDate(startDate).toFormat("yyyy-MM-dd")
                );
                formik.setFieldValue(
                  "endDate",
                  DateTime.fromJSDate(endDate).toFormat("yyyy-MM-dd")
                );
                formik.submitForm();
              }}
            />
          </div>

          {/* Segunda fila */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <MultiSelect
              selectedOptions={subcategories.filter((s) =>
                formik.values.subcategories?.includes(s.id)
              )}
              setSelectedOptions={(
                selected: Array<{ id: string; name: string }>
              ) => {
                formik.setFieldValue(
                  "subcategories",
                  selected.map((s) => s.id)
                );
                formik.submitForm();
              }}
              options={subcategories}
              title="Subcategoría"
            />
            <Select
              name="currency"
              selected={currencies.find(
                (currency) => currency.value === formik.values.currency
              )}
              setSelected={(option: {
                id: string | null | number;
                name: string;
                value: string | number | null;
              }) => {
                formik.setFieldValue("currency", option.value);
                formik.submitForm();
              }}
              options={currencies}
              tittle="Divisa"
              textDescribe=""
            />
          </div>

          {/* Tercera fila */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <InputWithCornerHint
              label="Monto mínimo"
              name="minAmount"
              value={formik.values.minAmount}
              onChange={(e) => {
                formik.handleChange(e);
                formik.submitForm();
              }}
            />
            <InputWithCornerHint
              label="Monto máximo"
              name="maxAmount"
              value={formik.values.maxAmount}
              onChange={(e) => {
                formik.handleChange(e);
                formik.submitForm();
              }}
            />
            <Select
              name="branchId"
              selected={branches.find((o) => o.value == formik.values.branchId)}
              setSelected={(option: {
                id: string | null | number;
                name: string;
                value: string | number | null;
              }) => {
                formik.setFieldValue("branchId", option.value);
                formik.submitForm();
              }}
              options={branches}
              tittle="Filial"
              textDescribe=""
            />
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};
